import PostAddIcon from '@mui/icons-material/PostAdd';
import TuneIcon from '@mui/icons-material/Tune';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Box,
  ButtonBase,
  Checkbox,
  IconButton,
  lighten,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
} from '@mui/material';
import React, { memo } from 'react';
import { useSelector } from 'react-redux';

import InitialsAvatar from 'FRONTEND/components/InitialsAvatar';
import { selectCalendarActorId } from 'FRONTEND/reducers/calendarSlice';
import { formColors } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { getInitials } from 'FRONTEND/utils/initials';
import { CALENDAR_USER_SETTINGS_PATH } from 'FRONTEND/utils/routes';

const CalendarListItem = ({
  item,
  itemTooltipTitle,
  color,
  onCheckBoxChange = () => {},
  onItemClick = () => {},
  onBookClick,
  onSettingsClick = () => {},
  disableCheckbox,
  size = 'large',
  shouldShowSettings = true,
}) => {
  const expertCheckedList = useSelector((state) => state.expert.checkedList);
  const isChecked = expertCheckedList.includes(item.id);
  const isMultipleView = useSelector(state => !state.calendar.actorId && !state.calendar.actorType);
  const isExtendedSidePanel = useSelector(state => state.sidePanel.isExtended);
  const calendarActorId = useSelector(selectCalendarActorId);
  const isSelected = calendarActorId === item.id;

  const handleCheckBoxChange = (event) => {
    onCheckBoxChange(event.target.checked, item.id);
  };

  const handleItemClick = () => {
    onItemClick(item.id);
  };

  const handleBookClick = () => {
    onBookClick(item.id);
  };

  const handleSettingsClick = (event) => {
    if (window.location.pathname === CALENDAR_USER_SETTINGS_PATH) {
      event.preventDefault();
    }
    onSettingsClick(item.id);
  };

  const itemStyles = {
    borderRight: (isSelected || isMultipleView) && `5px solid ${isSelected ? formColors.selectedItem : color}`,
    backgroundColor: (isSelected || isMultipleView) && color && lighten(isSelected ? formColors.selectedItem : color, 0.9),
  };

  return (
    <ListItem key={item.id} sx={itemStyles}>
      {!disableCheckbox && isExtendedSidePanel && (
        <Box sx={{ paddingRight: '1px' }}>
          <Checkbox
            sx={{
              '&.MuiButtonBase-root': {
                color,
              },
            }}
            onChange={handleCheckBoxChange}
            checked={isChecked}
            icon={<VisibilityOff color={color} />}
            checkedIcon={<Visibility color={color} />}
            size='small'
            edge='start'
          />
        </Box>)}
      <ListItemIcon sx={{ minWidth: '32px' }}>
        <InitialsAvatar
          edge='start'
          size='small'
          picture={item.picture}
          initials={getInitials(item.alias)}
          color={color}
          onClick={size !== 'large' ? handleItemClick : null}
        />
      </ListItemIcon>
      {size === 'large' && (
        <>
          <Tooltip title={itemTooltipTitle} disableInteractive>
            <ButtonBase
              onClick={handleItemClick}
              sx={{
                marginRight: '52px',
                overflow: 'hidden',
                width: '100%',
                alignSelf: 'stretch',
                textAlign: 'left',
              }}
            >
              <ListItemText
                primaryTypographyProps={{ noWrap: true }}
              >
                {item.alias}
              </ListItemText>
            </ButtonBase>
          </Tooltip>

          {!item.is_service &&
            <ListItemSecondaryAction>
              {shouldShowSettings && (
                <Tooltip title={i18n._('User settings')}>
                  <IconButton
                    edge='end'
                    onClick={handleSettingsClick}
                    data-pjax
                    href={CALENDAR_USER_SETTINGS_PATH}
                    size='small'
                  >
                    <TuneIcon />
                  </IconButton>
                </Tooltip>
              )}
              {onBookClick && (
                <Tooltip title={i18n._('Book an appointment')}>
                  <IconButton
                    edge='end'
                    onClick={handleBookClick}
                    size='small'
                  >
                    <PostAddIcon />
                  </IconButton>
                </Tooltip>
              )}
            </ListItemSecondaryAction>}
        </>
      )}
    </ListItem>
  );
};

export default memo(CalendarListItem); // Prevent extra picture rendering by memoizing the component
